import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import PagePicker from '../../components/pros/PagePicker'
import SkillsTagInput from '../../components/pros/SkillsTagInput'
import { getProMe, getProProfileCompleteness, patchProMe, type ProCompanyOption } from '../../api/pros'
import { getProsConsoleSummary } from '../../api/prosConsole'
import { useMe } from '../../hooks/useMe'
import { getToken } from '../../lib/auth'
import ProPublicCard from '../components/ProPublicCard'
import type { ProCardViewModel } from '../components/types'

type ProMode = 'REMOTE' | 'LOCAL' | 'BOTH'

type FieldErrors = Record<string, string>

const CATEGORY_OPTIONS = ['Engineering', 'Design', 'Marketing', 'Video', 'Photography', 'Music', 'Business']

function normalizeSkillList(input: string[]) {
  const out: string[] = []
  const seen = new Set<string>()
  input.forEach((item) => {
    const token = String(item || '').trim().replace(/\s+/g, ' ')
    if (!token) return
    const key = token.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    out.push(token)
  })
  return out
}

function parseServerFieldErrors(payload: any): FieldErrors {
  if (payload?.field_errors && typeof payload.field_errors === 'object') return payload.field_errors as FieldErrors
  if (payload && typeof payload === 'object') return payload as FieldErrors
  return {}
}

export default function ProsProfileTab({
  onDirtyChange,
}: {
  onDirtyChange?: (dirty: boolean) => void
}) {
  const authed = Boolean(getToken())
  const { me } = useMe(authed)
  const publicCardPath = useMemo(() => {
    const publicId = String((me as any)?.profile?.public_id || '').trim()
    return publicId ? `/people/i/${encodeURIComponent(publicId)}` : '/people'
  }, [me])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [missing, setMissing] = useState<string[]>([])
  const [isDiscoverable, setIsDiscoverable] = useState(false)

  const [title, setTitle] = useState('')
  const [bio, setBio] = useState('')
  const [proMode, setProMode] = useState<ProMode>('REMOTE')
  const [radius, setRadius] = useState('25')
  const [serviceAreaText, setServiceAreaText] = useState('')
  const [hourlyRate, setHourlyRate] = useState('')
  const [startingPrice, setStartingPrice] = useState('')
  const [skills, setSkills] = useState<string[]>([])
  const [companyPage, setCompanyPage] = useState<ProCompanyOption | null>(null)
  const [availableNow, setAvailableNow] = useState(false)
  const [visibleInDiscovery, setVisibleInDiscovery] = useState(true)
  const [category, setCategory] = useState('')
  const [subcategory, setSubcategory] = useState('')
  const [profileHandle, setProfileHandle] = useState('')
  const [profileDisplayName, setProfileDisplayName] = useState('')
  const [ratingAvg, setRatingAvg] = useState<number | null>(null)
  const [ratingCount, setRatingCount] = useState<number | null>(null)
  const [jobsCount, setJobsCount] = useState<number | null>(null)
  const [earnedPixe, setEarnedPixe] = useState<string | number | bigint | null>(null)
  const initialRef = useRef<string>('')
  const loadedRef = useRef(false)
  const showRadius = proMode === 'LOCAL' || proMode === 'BOTH'

  const snapshot = useMemo(
    () =>
      JSON.stringify({
        title: title.trim(),
        bio: bio.trim(),
        proMode,
        radius: showRadius ? String(radius || '').trim() : '',
        serviceAreaText: showRadius ? serviceAreaText.trim() : '',
        hourlyRate: String(hourlyRate || '').trim(),
        startingPrice: String(startingPrice || '').trim(),
        skills: normalizeSkillList(skills),
        companyPageId: companyPage?.id || null,
        availableNow,
        visibleInDiscovery,
        category: category.trim(),
        subcategory: subcategory.trim(),
      }),
    [title, bio, proMode, showRadius, radius, serviceAreaText, hourlyRate, startingPrice, skills, companyPage, availableNow, visibleInDiscovery, category, subcategory],
  )

  const dirty = loadedRef.current && initialRef.current !== snapshot

  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])

  useEffect(() => {
    const onBeforeUnload = (evt: BeforeUnloadEvent) => {
      if (!dirty) return
      evt.preventDefault()
      evt.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  const refreshCompleteness = async () => {
    try {
      const payload = await getProProfileCompleteness()
      setIsDiscoverable(Boolean(payload.is_discoverable))
      setMissing(Array.isArray(payload.missing) ? payload.missing : [])
    } catch {
      setIsDiscoverable(false)
      setMissing([])
    }
  }

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    Promise.all([getProMe(), getProProfileCompleteness(), getProsConsoleSummary().catch(() => null)])
      .then(([payload, completeness, summary]) => {
        if (cancelled) return
        const profile = payload.pro_profile
        const summaryPro = summary?.pro as any
        setProfileHandle(String((payload as any)?.profile?.handle || (payload as any)?.handle || summaryPro?.handle || ''))
        setProfileDisplayName(String((payload as any)?.profile?.display_name || (payload as any)?.display_name || summaryPro?.display_name || ''))
        setTitle(profile?.title || '')
        setBio(profile?.bio || '')
        setProMode((profile?.pro_mode as ProMode) || 'REMOTE')
        setRadius(String(profile?.service_radius_miles ?? 25))
        setServiceAreaText(profile?.service_area_text || '')
        setHourlyRate(profile?.hourly_rate_cents != null ? String(Math.round((profile.hourly_rate_cents || 0) / 100)) : '')
        setStartingPrice(profile?.base_rate_cents != null ? String(Math.round((profile.base_rate_cents || 0) / 100)) : '')
        setSkills(normalizeSkillList((profile?.skills_data || []).map((s) => s.name)))
        setAvailableNow(Boolean(profile?.available_now))
        setVisibleInDiscovery(Boolean(profile?.visible_in_discovery ?? true))
        setCategory(String(profile?.category || ''))
        setSubcategory(String(profile?.subcategory || ''))
        setCompanyPage(
          profile?.company
            ? {
                id: Number(profile.company.id),
                slug: profile.company.slug,
                title: profile.company.title,
                avatar_url: profile.company.avatar_url,
              }
            : null,
        )
        const ratingValue =
          profile?.average_rating != null
            ? Number(profile.average_rating)
            : summaryPro?.rating_avg != null
              ? Number(summaryPro.rating_avg)
              : null
        const countValue =
          profile?.rating_count != null
            ? Number(profile.rating_count)
            : summaryPro?.rating_count != null
              ? Number(summaryPro.rating_count)
              : null
        const completedJobsValue =
          profile?.completed_jobs_count != null
            ? Number(profile.completed_jobs_count)
            : summaryPro?.jobs_count_lifetime != null
              ? Number(summaryPro.jobs_count_lifetime)
              : null
        setRatingAvg(Number.isFinite(ratingValue as number) ? ratingValue : null)
        setRatingCount(Number.isFinite(countValue as number) ? countValue : null)
        setJobsCount(Number.isFinite(completedJobsValue as number) ? completedJobsValue : null)
        setEarnedPixe(summaryPro?.earned_pixe_lifetime ?? 0)
        setIsDiscoverable(Boolean(completeness?.is_discoverable))
        setMissing(Array.isArray(completeness?.missing) ? completeness.missing : [])
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.response?.data?.detail || e?.message || 'Failed to load profile.')
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (loading) return
    initialRef.current = snapshot
    loadedRef.current = true
  }, [loading]) // intentionally only after initial load

  useEffect(() => {
    if (!success) return
    const timer = window.setTimeout(() => setSuccess(false), 1800)
    return () => window.clearTimeout(timer)
  }, [success])

  const localMissing = useMemo(() => {
    const values: string[] = []
    if (title.trim().length < 4) values.push('title')
    if (bio.trim().length < 30) values.push('description')
    if (normalizeSkillList(skills).length < 1) values.push('skills')
    if (!hourlyRate.trim() && !startingPrice.trim()) values.push('rate')
    if (showRadius) {
      const radiusNum = Number(radius || 0)
      if (!Number.isFinite(radiusNum) || radiusNum < 1) values.push('service radius')
    }
    return values
  }, [title, bio, skills, hourlyRate, startingPrice, showRadius, radius])

  const proCardPreview = useMemo<ProCardViewModel>(() => {
    const baseStats: ProCardViewModel = {
      handle: profileHandle || 'your-handle',
      display_name: profileDisplayName || title.trim() || 'Pro Name',
      title: '',
      description: '',
      hourly_rate_pixe: null,
      starting_price_pixe: null,
      mode: 'remote',
      available_now: false,
      rating_avg: ratingAvg,
      rating_count: ratingCount,
      jobs_count: jobsCount,
      earned_pixe: earnedPixe ?? 0,
      skills: [],
      is_owner: true,
      company_title: companyPage?.title || '',
    }
    return {
      ...baseStats,
      title: title.trim() || 'Your Pro title',
      description: bio.trim(),
      hourly_rate_pixe: hourlyRate ? Number(hourlyRate) : null,
      starting_price_pixe: startingPrice ? Number(startingPrice) : null,
      mode: proMode === 'LOCAL' || proMode === 'BOTH' ? 'onsite' : 'remote',
      available_now: availableNow,
      skills: normalizeSkillList(skills),
    }
  }, [profileHandle, profileDisplayName, title, bio, hourlyRate, startingPrice, proMode, availableNow, skills, ratingAvg, ratingCount, jobsCount, earnedPixe, companyPage])

  const validateBeforeSave = (): boolean => {
    const nextErrors: FieldErrors = {}
    if (visibleInDiscovery) {
      if (title.trim().length < 4) nextErrors.title = 'Title must be at least 4 characters.'
      if (bio.trim().length < 30) nextErrors.bio = 'Description must be at least 30 characters.'
      if (normalizeSkillList(skills).length < 1) nextErrors.skills = 'Add at least one skill.'
      const hourlyNum = Number(hourlyRate || 0)
      const startingNum = Number(startingPrice || 0)
      if (!hourlyRate.trim() && !startingPrice.trim()) {
        nextErrors.hourly_rate_cents = 'Set hourly rate or starting price.'
        nextErrors.base_rate_cents = 'Set starting price or hourly rate.'
      }
      if (hourlyRate.trim() && (!Number.isFinite(hourlyNum) || hourlyNum < 1 || hourlyNum > 100000)) {
        nextErrors.hourly_rate_cents = 'Hourly rate must be between 1 and 100000.'
      }
      if (startingPrice.trim() && (!Number.isFinite(startingNum) || startingNum < 1 || startingNum > 1000000)) {
        nextErrors.base_rate_cents = 'Starting price must be between 1 and 1000000.'
      }
      if (showRadius) {
        const radiusNum = Number(radius || 0)
        if (!Number.isFinite(radiusNum) || radiusNum < 1 || radiusNum > 500) {
          nextErrors.service_radius_miles = 'Radius must be between 1 and 500 miles.'
        }
      }
    }
    setFieldErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const onSave = async () => {
    if (!validateBeforeSave()) return
    setSaving(true)
    setError(null)
    setSuccess(false)
    try {
      await patchProMe({
        title: title.trim(),
        bio: bio.trim(),
        category: category.trim(),
        subcategory: subcategory.trim(),
        visible_in_discovery: visibleInDiscovery,
        pro_mode: proMode,
        service_radius_miles: showRadius ? Number(radius || 0) : 25,
        service_area_text: showRadius ? serviceAreaText.trim() : '',
        hourly_rate_cents: hourlyRate ? Number(hourlyRate) * 100 : null,
        base_rate_cents: startingPrice ? Number(startingPrice) * 100 : null,
        available_now: availableNow,
        skills: normalizeSkillList(skills),
        company_page_id: companyPage?.id ?? null,
      })
      initialRef.current = snapshot
      setFieldErrors({})
      setSuccess(true)
      await refreshCompleteness()
    } catch (e: any) {
      const payload = e?.response?.data
      setFieldErrors(parseServerFieldErrors(payload))
      setError(payload?.non_field_error || payload?.detail || e?.message || 'Failed to save profile.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="text-sm text-white/70">Loading profile...</div>

  return (
    <div className="space-y-4">
      <div className={`rounded-xl border p-3 ${isDiscoverable ? 'border-emerald-400/30 bg-emerald-500/10' : 'border-amber-300/30 bg-amber-500/10'}`}>
        <div className="text-sm font-semibold text-white">
          {isDiscoverable ? 'Your Pro card is visible in discovery.' : 'Complete your profile to appear in discovery.'}
        </div>
        {!isDiscoverable ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {(missing.length ? missing : localMissing).map((item) => (
              <span key={item} className="rounded-full border border-white/15 bg-black/25 px-2 py-0.5 text-xs text-white/80">
                {item}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <div className="space-y-3 rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-semibold text-white">Profile</div>
          <div className="text-xs text-white/60">Fields marked * are required for discovery.</div>
        </div>

        <label className="block text-sm">Title *
          <input value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => validateBeforeSave()} className={`mt-1 w-full rounded-lg border bg-black/30 px-3 py-2 ${fieldErrors.title ? 'border-red-400/60' : 'border-white/15'}`} />
          {fieldErrors.title ? <div className="mt-1 text-xs text-red-300">{fieldErrors.title}</div> : null}
        </label>

        <label className="block text-sm">Description *
          <textarea value={bio} onChange={(e) => setBio(e.target.value)} onBlur={() => validateBeforeSave()} className={`mt-1 min-h-24 w-full rounded-lg border bg-black/30 px-3 py-2 ${fieldErrors.bio ? 'border-red-400/60' : 'border-white/15'}`} />
          {fieldErrors.bio ? <div className="mt-1 text-xs text-red-300">{fieldErrors.bio}</div> : null}
        </label>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <label className="block text-sm">Category
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2">
              <option value="">Select category</option>
              {CATEGORY_OPTIONS.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
          </label>
          <label className="block text-sm">Subcategory
            <input value={subcategory} onChange={(e) => setSubcategory(e.target.value)} className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2" placeholder="Optional" />
          </label>
        </div>

        <label className="block text-sm">Mode *
          <select value={proMode} onChange={(e) => setProMode(e.target.value as ProMode)} className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2">
            <option value="REMOTE">Remote</option>
            <option value="LOCAL">On-site</option>
            <option value="BOTH">Remote + On-site</option>
          </select>
        </label>

        {showRadius ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="block text-sm">Service radius (miles) *
              <input type="number" min={1} value={radius} onChange={(e) => setRadius(e.target.value)} onBlur={() => validateBeforeSave()} className={`mt-1 w-full rounded-lg border bg-black/30 px-3 py-2 ${fieldErrors.service_radius_miles ? 'border-red-400/60' : 'border-white/15'}`} />
              {fieldErrors.service_radius_miles ? <div className="mt-1 text-xs text-red-300">{fieldErrors.service_radius_miles}</div> : null}
            </label>
            <label className="block text-sm">Service area
              <input value={serviceAreaText} onChange={(e) => setServiceAreaText(e.target.value)} className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2" placeholder="City, state or neighborhood" />
            </label>
          </div>
        ) : (
          <div className="text-xs text-white/60">Service radius hidden for remote work.</div>
        )}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <label className="block text-sm">Hourly rate (PIXE/hr) *
            <input type="number" min={1} value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)} onBlur={() => validateBeforeSave()} className={`mt-1 w-full rounded-lg border bg-black/30 px-3 py-2 ${fieldErrors.hourly_rate_cents ? 'border-red-400/60' : 'border-white/15'}`} />
            <div className="mt-1 text-xs text-white/60">Hourly is best for ongoing work.</div>
            {fieldErrors.hourly_rate_cents ? <div className="mt-1 text-xs text-red-300">{fieldErrors.hourly_rate_cents}</div> : null}
          </label>
          <label className="block text-sm">Starting price (PIXE) *
            <input type="number" min={1} value={startingPrice} onChange={(e) => setStartingPrice(e.target.value)} onBlur={() => validateBeforeSave()} className={`mt-1 w-full rounded-lg border bg-black/30 px-3 py-2 ${fieldErrors.base_rate_cents ? 'border-red-400/60' : 'border-white/15'}`} />
            <div className="mt-1 text-xs text-white/60">Starting price is best for fixed-price projects.</div>
            {fieldErrors.base_rate_cents ? <div className="mt-1 text-xs text-red-300">{fieldErrors.base_rate_cents}</div> : null}
          </label>
        </div>

        <label className="block text-sm">Skills *
          <SkillsTagInput value={skills} onChange={(next) => setSkills(() => normalizeSkillList(next))} />
          <div className="mt-1 text-xs text-white/60">Add 3-10 skills for best results. Duplicates are removed automatically.</div>
          {fieldErrors.skills ? <div className="mt-1 text-xs text-red-300">{fieldErrors.skills}</div> : null}
        </label>

        <label className="block text-sm">Company link
          <PagePicker value={companyPage} onSelect={setCompanyPage} />
        </label>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <label className="inline-flex items-start gap-2 text-sm">
            <input type="checkbox" checked={visibleInDiscovery} onChange={(e) => setVisibleInDiscovery(e.target.checked)} />
            <span>
              <span className="font-medium">Visible in discovery</span>
              <span className="mt-0.5 block text-xs text-white/60">Turn off to hide your card from search while keeping existing jobs.</span>
            </span>
          </label>
          <label className="inline-flex items-start gap-2 text-sm">
            <input type="checkbox" checked={availableNow} onChange={(e) => setAvailableNow(e.target.checked)} />
            <span>
              <span className="font-medium">Available now</span>
              <span className="mt-0.5 block text-xs text-white/60">Affects ranking and labels. It is not a guaranteed response time.</span>
            </span>
          </label>
        </div>

        {error ? <div className="rounded-lg border border-red-400/30 bg-red-500/10 p-2 text-sm text-red-300">{error}</div> : null}
        {success ? <div className="rounded-lg border border-emerald-400/30 bg-emerald-500/10 p-2 text-sm text-emerald-200">Profile saved.</div> : null}

        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={() => void onSave()} disabled={saving} className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60">
            {saving ? 'Saving...' : 'Save profile'}
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="text-sm font-semibold text-white/90">Pro card preview</div>
          <Link to={publicCardPath} target="_blank" rel="noreferrer" className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-white hover:bg-white/10">
            View public card
          </Link>
        </div>
        <div className="rounded-xl border border-white/10 bg-black/20 p-3">
          <ProPublicCard pro={proCardPreview} variant="preview" show_book_cta />
        </div>
      </div>
    </div>
  )
}
